import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/auth-context';
import { createClient } from '@/lib/supabase';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, Users, Coins, Play } from 'lucide-react';
import toast from 'react-hot-toast';
import { Navbar } from '@/components/Navbar';

export default function OnlinePage() {
  const navigate = useNavigate();
  const { user, updateChips } = useAuth();
  const [searching, setSearching] = useState(false);
  const [selectedChips, setSelectedChips] = useState<number | null>(null);
  const supabase = createClient();

  // Buscar o crear partida amb IDs predeterminats segons les fitxes
  const findGame = useCallback(async (initialChips: number, maxRounds: number) => {
    if (!user || searching) return;
    
    setSearching(true);
    setSelectedChips(initialChips);
    
    try {
      // Obtenir perfil
      const { data: profile } = await supabase
        .from('profiles')
        .select('id, chips')
        .eq('username', user.username)
        .single();

      if (!profile) {
        toast.error('Error al carregar el perfil');
        setSearching(false);
        setSelectedChips(null);
        return;
      }

      console.log('🎮 ONLINE - ENTRADA:', {
        username: user.username,
        profileChips: profile.chips,
        buyIn: initialChips,
        remaining: profile.chips - initialChips
      });

      // Verificar fitxes mínimes
      if (profile.chips < initialChips) {
        toast.error(`Necessites almenys ${initialChips} fitxes per jugar aquesta partida`);
        setSearching(false);
        setSelectedChips(null);
        return;
      }

      let gameId: string | null = null;
      let playerIndex = 0;

      // Provar partides en seqüència (partida1100, partida1200, partida1500...)
      for (let i = 1; i <= 100; i++) {
        const roomName = `partida${i}${initialChips}`;
        
        // Comprovar si existeix aquesta partida
        const { data: game } = await supabase
          .from('online_games')
          .select('id')
          .eq('room_name', roomName)
          .maybeSingle();

        if (!game) {
          // La partida no existeix, crear-la
          const { data: newGame, error: createError } = await supabase
            .from('online_games')
            .insert({
              room_name: roomName,
              status: 'waiting',
              initial_chips: initialChips,
              max_rounds: maxRounds,
              current_round: 1
            })
            .select('id')
            .single();

          if (createError) {
            console.error('Error creant partida:', createError);
            // Si hi ha error per duplicat, potser un altre usuari l'acaba de crear
            // Intentem llegir-la de nou
            const { data: retryGame } = await supabase
              .from('online_games')
              .select('id')
              .eq('room_name', roomName)
              .maybeSingle();
            
            if (retryGame) {
              const { count } = await supabase
                .from('game_players')
                .select('*', { count: 'exact', head: true })
                .eq('game_id', retryGame.id);

              if ((count || 0) < 2) {
                gameId = retryGame.id;
                playerIndex = count || 0;
                break;
              }
            }
            // Si no es pot recuperar, continuar amb la següent
            continue;
          }
          
          if (newGame) {
            gameId = newGame.id;
            playerIndex = 0;
            break;
          }
        } else {
          // La partida existeix, comprovar si té espai
          const { count } = await supabase
            .from('game_players')
            .select('*', { count: 'exact', head: true })
            .eq('game_id', game.id);

          if ((count || 0) < 2) {
            // Hi ha espai, unir-se
            gameId = game.id;
            playerIndex = count || 0;
            break;
          }
          // Si està plena, continuar amb la següent
        }
      }

      if (!gameId) {
        toast.error('No s\'ha pogut trobar cap partida disponible');
        setSearching(false);
        setSelectedChips(null);
        return;
      }

      // Verificar si el jugador ja existeix a la partida
      const { data: existingPlayer } = await supabase
        .from('game_players')
        .select('user_id, chips_before_buy_in, buy_in_paid')
        .eq('game_id', gameId)
        .eq('user_id', user.username)
        .maybeSingle();

      // Guardar les fitxes ABANS del buy-in (sempre guardem el valor original)
      const chipsBeforeBuyIn = profile.chips;

      // Unir-se a la partida PRIMER (abans de descomptar fitxes)
      const playerData: any = {
        game_id: gameId,
        user_id: user.username,
        profile_id: profile.id,
        player_index: playerIndex
      };
      
      // Afegir camps opcionals només si existeixen a la BD
      try {
        // Només actualitzar chips_before_buy_in si no existeix o si és null
        if (!existingPlayer || existingPlayer.chips_before_buy_in === null) {
          playerData.chips_before_buy_in = chipsBeforeBuyIn;
        }
        // Marcar que el buy-in encara no s'ha pagat (es pagarà quan comenci la partida)
        playerData.buy_in_paid = false;
      } catch (e) {
        // Si les columnes no existeixen, continuar sense elles
      }

      const { error: joinError } = await supabase
        .from('game_players')
        .upsert(playerData, {
          onConflict: 'game_id,user_id'
        });

      if (joinError) {
        console.error('Error unint-se a la partida:', joinError);
        toast.error('Error al unir-se a la partida');
        setSearching(false);
        setSelectedChips(null);
        return;
      }

      // Si és el segon jugador, la partida comença: descomptar buy-in de TOTS els jugadors
      if (playerIndex === 1) {
        // Obtenir tots els jugadors de la partida
        const { data: allPlayers, error: playersError } = await supabase
          .from('game_players')
          .select('user_id, profile_id, chips_before_buy_in, buy_in_paid')
          .eq('game_id', gameId);

        if (playersError || !allPlayers) {
          console.error('Error obtenint jugadors:', playersError);
          toast.error('Error al iniciar la partida');
          setSearching(false);
          setSelectedChips(null);
          return;
        }

        // Descomptar buy-in de tots els jugadors (només si initialChips > 0 i encara no s'ha pagat)
        if (initialChips > 0) {
          for (const player of allPlayers) {
            // Només descomptar si encara no s'ha pagat el buy-in
            if (!player.buy_in_paid && player.chips_before_buy_in !== null) {
              const newChips = player.chips_before_buy_in - initialChips;
              
              // Actualitzar fitxes del jugador
              const { error: updateChipsError } = await supabase
                .from('profiles')
                .update({ chips: newChips })
                .eq('id', player.profile_id);

              if (updateChipsError) {
                console.error(`Error actualitzant fitxes per ${player.user_id}:`, updateChipsError);
                // Continuar amb els altres jugadors encara que falli un
              } else {
                // Marcar que el buy-in s'ha pagat
                await supabase
                  .from('game_players')
                  .update({ buy_in_paid: true })
                  .eq('game_id', gameId)
                  .eq('user_id', player.user_id);

                console.log('💰 ONLINE - Buy-in descomptat quan la partida comença:', {
                  username: player.user_id,
                  before: player.chips_before_buy_in,
                  buyIn: initialChips,
                  after: newChips
                });

                // Actualitzar el context si és el jugador actual
                if (player.user_id === user.username) {
                  await updateChips(newChips);
                }
              }
            }
          }
        } else {
          // Partida gratuïta: no es descompta res
          console.log('🎮 ONLINE - Partida gratuïta (initialChips = 0), no es descompta res');
        }

        // Canviar status a in_progress
        await supabase
          .from('online_games')
          .update({ status: 'in_progress', started_at: new Date().toISOString() })
          .eq('id', gameId);
      } else {
        // Primer jugador: només esperar, no descomptar encara
        console.log('⏳ ONLINE - Esperant segon jugador, buy-in es descomptarà quan comenci la partida');
      }

      // Navegar al joc
      navigate('/online/game', { state: { gameId } });
    } catch (error: any) {
      console.error('Error:', error);
      toast.error('Error al buscar partida');
      setSearching(false);
      setSelectedChips(null);
    }
  }, [user, supabase, navigate, searching]);

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center p-4">
      <Navbar />
      <div className="w-full max-w-md space-y-6 pt-24">
        <div className="text-center">
          <h1 className="text-4xl font-black text-white mb-2">Mode Online</h1>
          <p className="text-zinc-400">Juga contra altres jugadors</p>
        </div>

        <Card className="border-zinc-800 bg-zinc-900/80 backdrop-blur-xl">
          <CardContent className="pt-6 space-y-6">
            <div className="text-center space-y-4">
              <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                <Users className="w-10 h-10 text-white" />
              </div>
              
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">Partida Online</h2>
                <p className="text-sm text-zinc-400">Selecciona el tipus de partida</p>
              </div>

              <div className="flex items-center justify-center gap-2 text-zinc-400 text-sm">
                <Coins className="w-4 h-4 text-amber-400" />
                <span>Les teves fitxes: <span className="text-white font-semibold">{user.chips}</span></span>
              </div>
            </div>

            {/* Tipus de partides */}
            <div className="space-y-3">
              {/* Partida Bàsica: 200 fitxes, 3 rondes */}
              <button
                onClick={() => findGame(200, 3)}
                disabled={searching || user.chips < 200}
                className="w-full p-4 rounded-xl border-2 border-zinc-700 bg-zinc-800/50 hover:border-blue-500 hover:bg-zinc-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-left group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-blue-500/20 border border-blue-500/30 flex items-center justify-center group-hover:bg-blue-500/30 transition-colors">
                      <Coins className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="font-bold text-white text-lg">Bàsica</h3>
                      <p className="text-xs text-zinc-400">200 fitxes • 3 rondes</p>
                    </div>
                  </div>
                  {searching && selectedChips === 200 ? (
                    <Loader2 className="w-5 h-5 text-blue-400 animate-spin" />
                  ) : (
                    <Play className="w-5 h-5 text-zinc-500 group-hover:text-blue-400 transition-colors" />
                  )}
                </div>
              </button>

              {/* Partida Estàndard: 500 fitxes, 5 rondes */}
              <button
                onClick={() => findGame(500, 5)}
                disabled={searching || user.chips < 500}
                className="w-full p-4 rounded-xl border-2 border-zinc-700 bg-zinc-800/50 hover:border-emerald-500 hover:bg-zinc-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-left group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center group-hover:bg-emerald-500/30 transition-colors">
                      <Coins className="w-6 h-6 text-emerald-400" />
                    </div>
                    <div>
                      <h3 className="font-bold text-white text-lg">Estàndard</h3>
                      <p className="text-xs text-zinc-400">500 fitxes • 5 rondes</p>
                    </div>
                  </div>
                  {searching && selectedChips === 500 ? (
                    <Loader2 className="w-5 h-5 text-emerald-400 animate-spin" />
                  ) : (
                    <Play className="w-5 h-5 text-zinc-500 group-hover:text-emerald-400 transition-colors" />
                  )}
                </div>
              </button>

              {/* Partida Premium: 1000 fitxes, 10 rondes */}
              <button
                onClick={() => findGame(1000, 10)}
                disabled={searching || user.chips < 1000}
                className="w-full p-4 rounded-xl border-2 border-zinc-700 bg-zinc-800/50 hover:border-amber-500 hover:bg-zinc-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-left group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center group-hover:bg-amber-500/30 transition-colors">
                      <Coins className="w-6 h-6 text-amber-400" />
                    </div>
                    <div>
                      <h3 className="font-bold text-white text-lg">Premium</h3>
                      <p className="text-xs text-zinc-400">1.000 fitxes • 10 rondes</p>
                    </div>
                  </div>
                  {searching && selectedChips === 1000 ? (
                    <Loader2 className="w-5 h-5 text-amber-400 animate-spin" />
                  ) : (
                    <Play className="w-5 h-5 text-zinc-500 group-hover:text-amber-400 transition-colors" />
                  )}
                </div>
              </button>
            </div>

            <div className="pt-4 border-t border-zinc-800">
              <p className="text-xs text-zinc-500 text-center">
                Seràs emparellat automàticament amb un altre jugador
              </p>
            </div>
          </CardContent>
        </Card>

        <Button
          onClick={() => navigate('/inici')}
          variant="outline"
          className="w-full border-zinc-700 text-zinc-400 hover:bg-zinc-800"
        >
          Tornar
        </Button>
      </div>
    </div>
  );
}
